import React, { useMemo, useState } from 'react';
import { errorMessage, listProfilingCategories, saveProfilingSelection } from '../../api';
import { useAsyncData } from '../../hooks/useAsyncData';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import { Badge, Button, Card, CardBody, EmptyState, ErrorState, InlineAlert, Skeleton, cx, useToast } from '../../ui';
import type { ProfilingCategory, ProfilingQuestion, ProfilingSelection } from './profilingModel';
import { sameSelection, selectedInCategory, selectionFromSaved, selectionToPayload, toggleAnswer } from './profilingModel';
import { useWizard } from './WizardContext';

const COLLAPSED_ANSWERS = 10; // longer answer lists start collapsed; chosen answers always stay visible

/** One question as a compact card: question + how many answers are chosen, answers as small chips. */
function QuestionCard({ question, selection, onToggle }: { question: ProfilingQuestion; selection: ProfilingSelection; onToggle: (answerId: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const answers = question.profiling_answers || [];
  const chosen = answers.filter((a) => selection[a.id]).length;
  const visible = expanded ? answers : answers.filter((a, i) => i < COLLAPSED_ANSWERS || selection[a.id]);
  const hidden = answers.length - visible.length;
  const wide = answers.length > 12; // a long list gets the full width instead of one tall narrow column
  return (
    <fieldset className={cx('rounded-lg border border-line bg-surface px-3 pb-3 pt-2', wide && 'lg:col-span-2')}>
      <legend className="float-left mb-1.5 w-full text-[13px] font-semibold leading-snug text-ink">
        {question.profiling_questions}
        {chosen > 0 && <span className="ml-2 rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-semibold text-brand-800">{chosen} selected</span>}
      </legend>
      <div className="clear-both flex flex-wrap gap-1.5">
        {visible.map((a) => {
          const on = Boolean(selection[a.id]);
          return (
            <label key={a.id} className={cx('cursor-pointer rounded-full border px-2.5 py-1 text-[13px] font-medium leading-tight', on ? 'border-brand-600 bg-brand-50 text-brand-800' : 'border-line text-ink hover:bg-chip')}>
              <input type="checkbox" className="sr-only" checked={on} onChange={() => onToggle(a.id)} />
              {on && <span aria-hidden="true">✓ </span>}{a.profiling_answers}
            </label>
          );
        })}
        {(hidden > 0 || expanded) && answers.length > COLLAPSED_ANSWERS && (
          <button type="button" aria-expanded={expanded} onClick={() => setExpanded((e) => !e)} className="rounded-full px-2.5 py-1 text-[13px] font-semibold text-brand-700 hover:bg-brand-50">
            {expanded ? 'Show fewer' : `Show ${hidden} more`}
          </button>
        )}
      </div>
    </fieldset>
  );
}

/**
 * Profiling step.
 *  - Data: GET /global_profiling/get_all_categories returns every category WITH its questions and answers
 *    embedded (about 165 KB), so one call feeds this whole step. Categories without questions are hidden.
 *  - Saving: POST .../targetGroup/{id}/profiling with an array of answer ids. It REPLACES the selection and
 *    [] clears it. The group is read back with profilingAnswers: [{ id, profiling_answers }].
 */
export function StepProfiling() {
  const { project, targetGroup, reload, goStep } = useWizard();
  const { notify } = useToast();
  const categories = useAsyncData<ProfilingCategory[]>((signal) => listProfilingCategories({ signal }), []);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [formError, setFormError] = useState('');
  const [submitting, run] = useSubmitGuard();

  // What is saved, until the user edits; after the first edit the local selection takes over.
  const saved = useMemo(() => selectionFromSaved(targetGroup.profilingAnswers, categories.data), [targetGroup.profilingAnswers, categories.data]);
  const [edited, setEdited] = useState<ProfilingSelection | null>(null);
  const selection = edited || saved;
  const update = (fn: (sel: ProfilingSelection) => ProfilingSelection) => setEdited((prev) => fn(prev || saved));

  const usable = useMemo(() => (categories.data || []).filter((c) => (c.profiling_questions || []).length > 0), [categories.data]);
  const hiddenCount = (categories.data || []).length - usable.length;
  const current = usable.find((c) => c.id === activeId) || usable[0] || null;

  const total = Object.keys(selection).length;
  const dirty = !sameSelection(selection, targetGroup.profilingAnswers);

  const save = () => {
    setFormError('');
    if (!dirty) { goStep('quotas'); return undefined; } // nothing changed: do not call the server
    return run(async () => {
      try {
        await saveProfilingSelection(project.id, targetGroup.id, selectionToPayload(selection));
        await reload();
        notify(total ? 'Profiling saved' : 'Profiling cleared', { tone: 'ok' });
        goStep('quotas');
      } catch (err) {
        setFormError(errorMessage(err));
      }
    });
  };

  return (
    <Card>
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-ink">Profiling</h2>
            <p className="mt-1 text-sm text-muted">Optionally narrow the audience using global profiling questions. Respondents matching any chosen answer to a question qualify.</p>
          </div>
          <Badge tone={total ? 'brand' : 'neutral'}>{total ? `${total} answer${total === 1 ? '' : 's'} selected` : 'No profiling'}</Badge>
        </div>

        <div className="mt-5">
          {categories.loading && !categories.data ? <Skeleton className="h-40 w-full" /> : categories.error ? (
            <ErrorState title="Could not load profiling categories" message={errorMessage(categories.error)} onRetry={categories.reload} />
          ) : usable.length === 0 ? (
            <EmptyState title="No profiling questions" description="No global profiling questions are available yet." />
          ) : (
            <div className="grid gap-4 md:grid-cols-[220px_1fr]">
              <div>
                <ul aria-label="Profiling categories" className="space-y-1">
                  {usable.map((c) => {
                    const on = current && c.id === current.id;
                    const n = selectedInCategory(selection, c.id);
                    return (
                      <li key={c.id}>
                        <button type="button" aria-current={on ? 'true' : undefined} onClick={() => setActiveId(c.id)}
                          className={cx('flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-medium', on ? 'bg-brand-50 font-semibold text-brand-800' : 'text-ink hover:bg-chip')}>
                          {c.categoryName}
                          {n > 0 && <Badge tone="brand">{n}</Badge>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {hiddenCount > 0 && <p className="mt-3 px-3 text-xs text-muted">{hiddenCount} categor{hiddenCount === 1 ? 'y has' : 'ies have'} no questions yet.</p>}
              </div>

              <div className="grid min-h-[8rem] content-start gap-3 lg:grid-cols-2">
                {(current?.profiling_questions || []).map((q) => (
                  <QuestionCard key={q.id} question={q} selection={selection} onToggle={(answerId) => update((sel) => toggleAnswer(sel, current!.id, q.id, answerId))} />
                ))}
              </div>
            </div>
          )}
        </div>

        {formError && <div className="mt-4"><InlineAlert>{formError}</InlineAlert></div>}
        <div className="mt-6 flex justify-between">
          <Button variant="secondary" onClick={() => goStep('regions')}>← Back</Button>
          <div className="flex gap-2">
            {total > 0 && <Button variant="ghost" onClick={() => setEdited({})}>Clear all</Button>}
            <Button onClick={save} loading={submitting}>{dirty ? 'Save & continue →' : total ? 'Continue →' : 'Skip profiling →'}</Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
